import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWeb3 } from '../../context/Web3Context';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Menu, X, Wallet, GraduationCap, Users, Calculator, 
  LogOut, User, ChevronDown, Coins, Home, BarChart3,
  Droplets, Lock, PieChart, Gift
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_94b15709-33c0-4b3c-a307-474349afd7f8/artifacts/vb9rqttx_ChatGPT%20Image%20Feb%201%2C%202026%20at%2008_39_11%20AM.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { isConnected, account, balance, walletType, disconnectWallet, connectWallet } = useWeb3();
  const location = useLocation();
  const navigate = useNavigate();

  // Main navigation - Financial Platform focus
  const mainNavLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/token-offering', label: 'Token Sale', icon: Coins },
    { to: '/staking', label: 'Staking', icon: Lock },
    { to: '/liquidity', label: 'Liquidity', icon: Droplets },
    { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  ];

  // Secondary navigation (dropdown for more)
  const moreLinks = [
    { to: '/treasury', label: 'Treasury', icon: PieChart },
    { to: '/academy', label: 'Academy', icon: GraduationCap },
    { to: '/community', label: 'Community', icon: Users },
    { to: '/calculator', label: 'Calculator', icon: Calculator },
  ];

  const handleLogout = () => {
    logout();
    disconnectWallet();
    navigate('/');
  };

  const truncateAddress = (addr) => {
    if (!addr) return '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="AIH Universe" className="h-10 w-10 rounded-lg" />
            <span className="font-unbounded font-bold text-lg hidden sm:block">
              <span className="text-white">AIH</span>
              <span className="text-[#D4AF37]"> Universe</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {mainNavLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`nav-link flex items-center gap-2 ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon size={16} />
                  {link.label}
                </Link>
              );
            })}
            
            {/* More Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="nav-link flex items-center gap-2" data-testid="nav-more">
                  More <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-[#0A1628] border-white/10">
                {moreLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <DropdownMenuItem key={link.to} asChild>
                      <Link to={link.to} className="flex items-center gap-2">
                        <Icon size={16} />
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Auth & Wallet Section */}
          <div className="hidden md:flex items-center gap-3">
            {/* Web3 Wallet Connection */}
            {isConnected ? (
              <div className="flex items-center gap-2 glass-panel px-3 py-1.5 rounded-lg" data-testid="wallet-connected">
                <div className="w-2 h-2 bg-green-400 rounded-full" />
                <span className="text-white text-sm font-mono">{truncateAddress(account)}</span>
                <span className="text-[#D4AF37] text-xs">
                  {parseFloat(balance).toFixed(4)} {walletType === 'unisat' ? 'BTC' : 'ETH'}
                </span>
              </div>
            ) : (
              <Button 
                onClick={() => connectWallet('metamask')}
                variant="outline" 
                size="sm"
                className="btn-secondary flex items-center gap-2"
                data-testid="connect-wallet-nav"
              >
                <Wallet size={16} />
                Connect
              </Button>
            )}

            {/* User Auth */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center">
                      <User size={16} className="text-black" />
                    </div>
                    <span className="text-white hidden lg:block">{user?.username}</span>
                    <ChevronDown size={16} className="text-white/60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#0A1628] border-white/10">
                  <div className="px-3 py-2">
                    <p className="text-sm text-white">{user?.full_name || user?.username}</p>
                    <p className="text-xs text-white/60">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem className="flex items-center gap-2 text-[#D4AF37]" data-testid="user-tokens">
                    <Coins size={16} />
                    <span>{user?.aih_tokens?.toLocaleString()} AIH</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-dashboard">
                      <User size={16} />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/wallet" className="flex items-center gap-2">
                      <Wallet size={16} />
                      My Wallet
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/referral" className="flex items-center gap-2">
                      <Gift size={16} />
                      Referrals
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="flex items-center gap-2 text-red-400"
                    data-testid="logout-btn"
                  >
                    <LogOut size={16} />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-white" data-testid="nav-login">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="btn-primary" data-testid="nav-register">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden glass-panel border-t border-white/10" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {[...mainNavLinks, ...moreLinks].map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`nav-link flex items-center gap-3 w-full ${isActive ? 'active' : ''}`}
                  onClick={() => setIsOpen(false)}
                >
                  <Icon size={20} />
                  {link.label}
                </Link>
              );
            })}
            
            <div className="border-t border-white/10 pt-4 mt-4">
              {/* Mobile Wallet */}
              {isConnected ? (
                <div className="glass-panel p-3 rounded-lg mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full" />
                    <span className="text-white text-sm font-mono">{truncateAddress(account)}</span>
                  </div>
                  <p className="text-[#D4AF37] text-sm mt-1">
                    {parseFloat(balance).toFixed(4)} {walletType === 'unisat' ? 'BTC' : 'ETH'}
                  </p>
                </div>
              ) : (
                <Button 
                  onClick={() => { connectWallet('metamask'); setIsOpen(false); }}
                  className="w-full btn-secondary mb-3"
                >
                  <Wallet size={18} className="mr-2" />
                  Connect Wallet
                </Button>
              )}
              
              {/* Mobile Auth */}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="nav-link flex items-center gap-3 w-full"
                    onClick={() => setIsOpen(false)}
                  >
                    <User size={20} />
                    Dashboard
                  </Link>
                  <Link
                    to="/wallet"
                    className="nav-link flex items-center gap-3 w-full"
                    onClick={() => setIsOpen(false)}
                  >
                    <Wallet size={20} />
                    My Wallet
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setIsOpen(false); }}
                    className="nav-link flex items-center gap-3 w-full text-red-400"
                  >
                    <LogOut size={20} />
                    Logout
                  </button>
                </>
              ) : (
                <div className="space-y-2">
                  <Link to="/login" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full btn-secondary">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setIsOpen(false)}>
                    <Button className="w-full btn-primary">
                      Get Started
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
